// frontend/src/pages/admin/GroupsPage.tsx

import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Space, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as groupsService from '../../services/groups';

const GroupsPage: React.FC = () => {
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const response = await groupsService.adminListGroups();
      if (response.success) {
        setGroups(response.data || []);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '加载分组失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingGroup(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingGroup(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await groupsService.adminDeleteGroup(id);
      message.success('删除成功');
      loadGroups();
    } catch (error: any) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingGroup) {
        await groupsService.adminUpdateGroup(editingGroup.id, values);
        message.success('更新成功');
      } else {
        await groupsService.adminCreateGroup(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadGroups();
    } catch (error: any) {
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleConfigureFee = (record: any) => {
    navigate(`/admin/groups/${record.id}/fee-config`);
  };

  const columns = [
    {
      title: '分组名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <Space>
          {record.icon && <span>{record.icon}</span>}
          <span>{text}</span>
          {record.color && <Tag color={record.color}>{record.color}</Tag>}
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '商户数量',
      dataIndex: 'merchant_count',
      key: 'merchant_count',
      align: 'center' as const,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<SettingOutlined />}
            onClick={() => handleConfigureFee(record)}
          >
            费率配置
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个分组吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>商户分组管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          创建分组
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={groups || []}
        rowKey="id"
        loading={loading}
        locale={{ emptyText: '暂无数据' }}
      />

      <Modal
        title={editingGroup ? '编辑分组' : '创建分组'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="分组名称"
            name="name"
            rules={[{ required: true, message: '请输入分组名称' }]}
          >
            <Input placeholder="请输入分组名称" />
          </Form.Item>

          <Form.Item label="描述" name="description">
            <Input.TextArea rows={3} placeholder="请输入分组描述" />
          </Form.Item>

          <Form.Item label="图标" name="icon">
            <Input placeholder="请输入图标 (emoji)" />
          </Form.Item>

          <Form.Item label="颜色" name="color">
            <Input placeholder="请输入颜色代码 (如: #1890ff)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GroupsPage;
